/**
 * labResults.ts — Normalización y clasificación de resultados de laboratorio.
 *
 * Las respuestas de MedisysApi/MINSA traen esquemas variables; este módulo:
 *   - Extrae el NOMBRE del analito de forma robusta (arregla el bug de mostrar
 *     "Resultado" cuando el campo no coincide con una llave conocida).
 *   - Parsea el valor numérico y el rango de referencia (min/max).
 *   - Clasifica el estado (crítico / alto / bajo / normal / desconocido) para
 *     dirigir la atención del médico a los valores fuera de rango.
 *   - Ordena los resultados poniendo los ALTERADOS primero.
 */

export type LabEstado = 'critico' | 'alto' | 'bajo' | 'normal' | 'desconocido';

export interface NormalizedLabResult {
  nombre: string;
  valor: string;            // valor para mostrar (tal cual)
  valorNum: number | null;
  unidad: string;
  rango: string;            // rango para mostrar
  rangoMin: number | null;
  rangoMax: number | null;
  estado: LabEstado;
  flagTexto: string;        // bandera original de la API, si la trae
}

function num(s: string | null | undefined): number | null {
  if (!s) return null;
  const m = String(s).replace(',', '.').match(/-?\d+(?:\.\d+)?/);
  if (!m) return null;
  const n = parseFloat(m[0]);
  return Number.isFinite(n) ? n : null;
}

function pickStr(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

const NAME_KEYS = [
  'analito', 'nombreAnalito', 'prueba', 'nombrePrueba', 'examen', 'nombreExamen',
  'analisis', 'nombreAnalisis', 'parametro', 'nombreParametro', 'componente',
  'nombreComponente', 'estudio', 'nombreEstudio', 'test', 'nombreTest',
  'determinacion', 'denominacion', 'descripcionPrueba', 'glosa', 'item',
  'nombre', 'descripcion',
];

/** Llaves que NO son el nombre (valor/unidad/rango/meta). */
const NOT_NAME_RE = /valor|resultad|value|unidad|unit|rango|referenc|flag|interpret|fecha|date|codigo|orden|sede|^id$|estado|status|metodo|hora/i;

/**
 * Extrae el nombre del analito: primero llaves conocidas; si no, heurística =
 * el campo string "más nombre" (con letras, longitud razonable) que no sea
 * valor/unidad/rango. Evita el genérico "Resultado".
 */
function extraerNombre(r: Record<string, unknown>): string {
  const conocido = pickStr(r, NAME_KEYS);
  if (conocido) return conocido;
  let best = '';
  for (const [k, v] of Object.entries(r)) {
    if (NOT_NAME_RE.test(k)) continue;
    if (typeof v === 'string') {
      const t = v.trim();
      if (/[a-záéíóúñ]/i.test(t) && t.length >= 3 && t.length <= 80 && t.length > best.length) {
        best = t;
      }
    }
  }
  return best || 'Parámetro';
}

/** Parsea un rango de referencia "10 - 20", "<5", ">100", "10 a 20". */
function parseRango(rango: string): [number | null, number | null] {
  if (!rango) return [null, null];
  const r = rango.replace(',', '.');
  let m = r.match(/(-?\d+(?:\.\d+)?)\s*(?:-|–|—|a|to|hasta)\s*(-?\d+(?:\.\d+)?)/i);
  if (m) return [parseFloat(m[1]), parseFloat(m[2])];
  m = r.match(/[<≤]\s*=?\s*(-?\d+(?:\.\d+)?)/);
  if (m) return [null, parseFloat(m[1])];
  m = r.match(/[>≥]\s*=?\s*(-?\d+(?:\.\d+)?)/);
  if (m) return [parseFloat(m[1]), null];
  return [null, null];
}

/** Clasifica el estado a partir de la bandera y/o la comparación con el rango. */
function clasificar(
  valorNum: number | null,
  min: number | null,
  max: number | null,
  flag: string,
): LabEstado {
  const f = flag.toLowerCase();
  if (/(crit|pánico|panico|panic|grave|alarma)/.test(f)) return 'critico';
  if (/(alto|alta|elevad|high|\bh\b|↑|aumentad)/.test(f)) return 'alto';
  if (/(bajo|baja|low|\bl\b|↓|disminuid|deficien)/.test(f)) return 'bajo';
  if (/(normal|adecuad|negativ|no\s*reactiv|\bn\b)/.test(f)) return 'normal';

  if (valorNum == null) return 'desconocido';
  if (max != null && valorNum > max) {
    return max !== 0 && valorNum > max * 1.5 ? 'critico' : 'alto';
  }
  if (min != null && valorNum < min) {
    return min !== 0 && valorNum < min * 0.5 ? 'critico' : 'bajo';
  }
  if (min != null || max != null) return 'normal';
  return 'desconocido';
}

export function normalizeLabResult(r: Record<string, unknown>): NormalizedLabResult {
  const nombre = extraerNombre(r);
  const valor = pickStr(r, ['valor', 'resultado', 'value', 'Valor', 'resultadoValor', 'valorResultado']);
  const unidad = pickStr(r, ['unidad', 'unidadMedida', 'unidadMedicion', 'unit', 'Unidad', 'um']);
  const rango = pickStr(r, ['rango', 'rangoReferencia', 'valorReferencia', 'valoresReferencia', 'referencia', 'Referencia', 'intervaloReferencia']);
  const flagTexto = pickStr(r, ['flag', 'interpretacion', 'indicador', 'Interpretacion', 'bandera', 'estado']);
  const valorNum = num(valor);
  const [rangoMin, rangoMax] = parseRango(rango);
  const estado = clasificar(valorNum, rangoMin, rangoMax, flagTexto);
  return { nombre, valor, valorNum, unidad, rango, rangoMin, rangoMax, estado, flagTexto };
}

const SEVERITY: Record<LabEstado, number> = {
  critico: 0, alto: 1, bajo: 1, normal: 3, desconocido: 2,
};

export function esAlterado(e: LabEstado): boolean {
  return e === 'critico' || e === 'alto' || e === 'bajo';
}

/** Ordena: alterados (crítico → alto/bajo) primero; conserva orden relativo. */
export function ordenarResultados(list: NormalizedLabResult[]): NormalizedLabResult[] {
  return list
    .map((r, i) => ({ r, i }))
    .sort((a, b) => SEVERITY[a.r.estado] - SEVERITY[b.r.estado] || a.i - b.i)
    .map((x) => x.r);
}
